import {assertUnreachable} from '@wandb/weave/common/util/types';

import {captureError} from '../../integrations';
import {ThunkResult} from '../../types/redux';
import * as ViewActionsInternal from '../views/actionsInternal';
import {View} from '../views/types';
import {load} from './actions';
import {
  createAndLoadDraftForExistingWorkspace,
  createNewWorkspaceAndLoadDraft,
  createNewWorkspaceAndLoadPublished,
  generateUserWorkspaceDisplayName,
} from './namedWorkspaceActionsInternal';
import {determineLoadActionForUrl} from './namedWorkspaceUrls';
import {
  createNamedWorkspaceId,
  getNamedWorkspaceId,
  NamedWorkspaceId,
  NW_PRIMARY_ID,
  NW_PUBLISHED_USERNAME,
} from './types';
import {WorkspaceUrl} from './url';

export const PRIMARY_WORKSPACE_DISPLAY_NAME = 'Primary';

// The actions in this file are the actions to call when a user indicates they want
// to interact with workspaces. For example, they might be called as result of the user clicking a button

/**
 * Main action to call when the user wants to create a new workspace
 */
export const createNewNamedWorkspace = (
  workspaceStateId: string
): ThunkResult<void> => {
  return async (dispatch, getState) => {
    const workspace = getState().workspaces[workspaceStateId];
    const viewer = getState().viewer.viewer;
    if (!viewer) {
      throw new Error('must have viewer to create named workspace');
    }
    if (!viewer.username) {
      captureError(
        'creating named workspace, but user does not have username',
        'createNewNamedWorkspace'
      );
      throw new Error('Must be a logged in user');
    }
    if (workspace.defaultSpec == null) {
      throw new Error('Invalid state');
    }
    const nwId = createNamedWorkspaceId(viewer.username, workspace.relatedId);

    const displayName = generateUserWorkspaceDisplayName(viewer.username);
    return dispatch(
      createNewWorkspaceAndLoadDraft(workspaceStateId, nwId, displayName)
    );
  };
};

/**
 * Main entry point for switching views displayed in the user's workspace UI
 */
export const loadNamedWorkspace = (
  workspaceStateId: string,
  viewCid: string,
  nwId: NamedWorkspaceId
): ThunkResult<void> => {
  return async (dispatch, getState, client) => {
    dispatch(ViewActionsInternal.deleteUndoRedoHistory());
    await dispatch(load(workspaceStateId, viewCid, nwId));
  };
};

export const loadNamedWorkspaceForUrl = (
  workspaceStateId: string,
  workspaceUrl: WorkspaceUrl,
  views: View[]
): ThunkResult<Promise<string | void>> => {
  return async (dispatch, getState) => {
    const viewerUsername = getState().viewer.viewer?.username;
    const workspace = getState().workspaces[workspaceStateId];
    const relatedId = workspace.relatedId;
    const action = determineLoadActionForUrl(
      workspaceUrl,
      viewerUsername,
      views,
      relatedId
    );
    switch (action.type) {
      case 'createPrimaryPublishedAndDraft':
        if (!viewerUsername) {
          throw new Error('createPrimaryPublishedAndDraft, but no username');
        }
        return dispatch(
          createNewWorkspaceAndLoadDraft(
            workspaceStateId,
            getNamedWorkspaceId(NW_PRIMARY_ID, viewerUsername, relatedId),
            PRIMARY_WORKSPACE_DISPLAY_NAME
          )
        );
      case 'createPrimaryDraft':
        return dispatch(
          createAndLoadDraftForExistingWorkspace(
            workspaceStateId,
            getNamedWorkspaceId(NW_PRIMARY_ID, action.username, relatedId),
            action.templateViewCid
          )
        );

      case 'createPrimaryPublished':
        /**
         * We should only be calling this when there's an anonymous user.
         * We don't drafts for anonymous users, BUT the primary workspace hasn't been created yet.
         * So we're going to create a local only primary workspace without a draft
         */
        return dispatch(
          createNewWorkspaceAndLoadPublished(
            workspaceStateId,
            getNamedWorkspaceId(
              NW_PRIMARY_ID,
              NW_PUBLISHED_USERNAME,
              relatedId
            ),
            PRIMARY_WORKSPACE_DISPLAY_NAME
          )
        );

      case 'load':
        return dispatch(
          loadNamedWorkspace(workspaceStateId, action.viewCid, action.nwId)
        );

      case 'createPublished':
        return dispatch(
          createNewWorkspaceAndLoadPublished(
            workspaceStateId,
            action.nwId,
            action.displayName
          )
        );
      case 'error':
        // In the future we will show the error in the main UI if appropriate
        throw new Error('load action was error' + JSON.stringify({action}));
      default:
        assertUnreachable(action);
    }
  };
};
